import { Injectable, effect, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface AppConfig {
    inputStyle: string;
    colorScheme: string;
    theme: string;
    ripple: boolean;
    menuMode: string;
    scale: number;
}

const SIDEBAR_RAIL_KEY = 'em-fe:sidebar-rail';

interface LayoutState {
    /** Desktop sidebar collapsed into the icon rail (remembered across reloads). */
    staticMenuDesktopInactive: boolean;
    /** Sidebar removed entirely, for users with nothing to navigate to. */
    staticMenuHidden: boolean;
    overlayMenuActive: boolean;
    profileSidebarVisible: boolean;
    configSidebarVisible: boolean;
    staticMenuMobileActive: boolean;
    menuHoverActive: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    _config: AppConfig = {
        ripple: false,
        inputStyle: 'outlined',
        menuMode: 'static',
        colorScheme: 'light',
        theme: 'lara-light-blue',
        scale: 14,
    };

    config = signal<AppConfig>(this._config);

    state: LayoutState = {
        staticMenuDesktopInactive: readSidebarRail(),
        staticMenuHidden: false,
        overlayMenuActive: false,
        profileSidebarVisible: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
    };

    private configUpdate = new Subject<AppConfig>();

    private overlayOpen = new Subject<any>();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();
    private isRestrictedModeSubject = new BehaviorSubject<boolean>(false);
    isRestrictedMode$ = this.isRestrictedModeSubject.asObservable();

    constructor() {
        const savedColorScheme = localStorage.getItem('colorScheme');
        if (savedColorScheme === 'dark' || savedColorScheme === 'light') {
            this._config = {
                ...this._config,
                colorScheme: savedColorScheme,
                theme:
                    savedColorScheme === 'dark'
                        ? 'lara-dark-blue'
                        : 'lara-light-blue',
            };
            this.config.set(this._config);
        }

        this.applyColorScheme(this._config.colorScheme);
        this.changeTheme();

        effect(() => {
            const config = this.config();
            const themeChanged = this.updateStyle(config);

            this.applyColorScheme(config.colorScheme);
            if (themeChanged) {
                this.changeTheme();
            }
            this.changeScale(config.scale);
            this.onConfigUpdate();
        });
    }

    setRestrictedMode(isRestricted: boolean) {
        this.isRestrictedModeSubject.next(isRestricted);
    }

    updateStyle(config: AppConfig) {
        return (
            config.theme !== this._config.theme ||
            config.colorScheme !== this._config.colorScheme
        );
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.state.overlayMenuActive = !this.state.overlayMenuActive;
            if (this.state.overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.state.staticMenuDesktopInactive =
                !this.state.staticMenuDesktopInactive;
            try {
                localStorage.setItem(SIDEBAR_RAIL_KEY, String(this.state.staticMenuDesktopInactive));
            } catch { /* storage may be unavailable: the choice just won't survive a reload */ }
        } else {
            this.state.staticMenuMobileActive =
                !this.state.staticMenuMobileActive;

            if (this.state.staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    hidSideBar() {
        if (this.isOverlay()) {
            this.state.overlayMenuActive = true;
            if (this.state.overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.state.staticMenuHidden = true;
        } else {
            this.state.staticMenuMobileActive = true;

            if (this.state.staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    showProfileSidebar() {
        this.state.profileSidebarVisible = !this.state.profileSidebarVisible;
        if (this.state.profileSidebarVisible) {
            this.overlayOpen.next(null);
        }
    }

    showConfigSidebar() {
        this.state.configSidebarVisible = true;
    }

    isOverlay() {
        return this.config().menuMode === 'overlay';
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.config() };
        localStorage.setItem('colorScheme', this._config.colorScheme);
        this.configUpdate.next(this.config());
    }

    toggleDarkMode(isDarkMode: boolean) {
        const colorScheme = isDarkMode ? 'dark' : 'light';

        this.config.update((config) => ({
            ...config,
            colorScheme,
            theme: isDarkMode ? 'lara-dark-blue' : 'lara-light-blue',
        }));
    }

    private applyColorScheme(colorScheme: string) {
        document.documentElement.classList.toggle(
            'app-dark',
            colorScheme === 'dark'
        );
        document.documentElement.style.colorScheme = colorScheme;
    }

    changeTheme() {
        const config = this.config();
        this.replaceThemeLink(
            `assets/primeng-themes/${config.theme}/theme.css`
        );
    }

    replaceThemeLink(href: string) {
        const id = 'theme-css';
        const themeLink = document.getElementById(id) as HTMLLinkElement | null;

        if (!themeLink || themeLink.getAttribute('href') === href) {
            return;
        }

        const cloneLinkElement = themeLink.cloneNode(true) as HTMLLinkElement;
        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', `${id}-clone`);

        themeLink.parentNode?.insertBefore(
            cloneLinkElement,
            themeLink.nextSibling
        );
        cloneLinkElement.addEventListener('load', () => {
            themeLink.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    changeScale(value: number) {
        document.documentElement.style.fontSize = `${value}px`;
    }
}

function readSidebarRail(): boolean {
    try {
        return localStorage.getItem(SIDEBAR_RAIL_KEY) === 'true';
    } catch {
        return false;
    }
}
